import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Skeleton,
  Button,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

// Web3 & Blockchain Services
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import { networkID } from "../../../../static/utils";
import { Path, getNetworkCurrency, DEFAULT_CURRENCY } from "../../../../static/Constants";
import getImageUrlByHash from "../../../../static/logoUtils";
import {
  getConnectedAccount,
  connectWallet as triggerConnectWallet,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";

// Common Components
import NavBar from "../../../Components/NavBar";
import Loaders from "../../../Components/Loaders";
import Warnings from "../../../Components/Warning";
import SuccessPopup from "../../../Components/SuccessPopup";
import FailedPopup from "../../../Components/FailedPopup";
import { borderRadiusPx } from "../../../../theme";

// Revamped BuyLottery Modular Subcomponents for ALL Lottery Types
import LotteryHeader from "./components/LotteryHeader";
import NumberPicker from "./components/NumberPicker";
import MinePicker from "./components/MinePicker";
import MissilePicker from "./components/MissilePicker";
import SpinWheelView from "./components/SpinWheelView";
import LotterySummaryCard from "./components/LotterySummaryCard";
import LotteryResultView from "./components/LotteryResultView";
import LotteryRulesInfo from "./components/LotteryRulesInfo";
import BuyLotteryEmptyState from "./components/BuyLotteryEmptyState";

import "./BuyLotteryStyle.css";

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const BuyLottery = (props) => {
  const params = useParams();
  const history = useHistory();
  const lotteryId = params.id || props.match?.params?.id;

  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [account, setAccount] = useState("");
  const [networkId, setNetworkId] = useState(networkID);

  const [lotterylist, setLotteryList] = useState({
    entryFee: 0,
    totalPrize: 0,
    pickNumbers: 3,
    capacity: 20,
    status: 0,
    lotteryType: "2",
  });
  const [datelist, setDateList] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  // Type-specific states
  const [selectednum, setSelectedNum] = useState([]); // MegaBET (array) or MineBET ([number])
  const [alreadySelectedMine, setAlreadySelectedMine] = useState([]); // MineBET sold tickets
  const [selectedMissileNumber, setSelectedMissileNumber] = useState(""); // RocketBET target

  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Lottery ticket bought successfully!");

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);

  // Initialize Account and listen to changes
  const checkAccount = useCallback(async () => {
    const acc = await getConnectedAccount();
    setAccount(acc);
    setHasError(false);
    if (window.ethereum) {
      const netId =
        window.ethereum.networkVersion ||
        (web3 && web3.eth && web3.eth.net
          ? await web3.eth.net.getId().catch(() => null)
          : null);
      if (netId) setNetworkId(netId);
    }
  }, []);

  const connectWallet = async () => {
    const res = await triggerConnectWallet();
    if (res.account) {
      setAccount(res.account);
      setHasError(false);
    } else if (res.error === "NO_WALLET") {
      toast.error(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  // Fetch Lottery Data from On-Chain Contract
  const fetchLotteryData = useCallback(async () => {
    setLoading(true);
    setHasError(false);

    try {
      if (window.ethereum) {
        window.web3 = web3;
      }

      let activeId = lotteryId;

      // Auto-discover active lottery if no lotteryId provided in URL
      if (!activeId && cinstance && cinstance.methods) {
        try {
          const path = window.location.pathname.toLowerCase();
          let targetType = 2;
          if (path.includes("mine")) targetType = 1;
          else if (path.includes("missile")) targetType = 3;
          else if (path.includes("spinner")) targetType = 0;

          const totalRaw = await cinstance.methods.lotteryId().call();
          const total = Number(totalRaw || 0);

          for (let i = total; i >= 1; i--) {
            try {
              const item = await cinstance.methods.lottery(i).call();
              if (
                item &&
                item.lotteryId &&
                item.lotteryId !== "0" &&
                (item.status === "0" || item.status === "5") &&
                Number(item.lotteryType) === targetType
              ) {
                activeId = `${i}`;
                break;
              }
            } catch (scanErr) {
              // ignore scan failure
            }
          }
        } catch (findErr) {
          console.warn("Could not auto-find active lottery:", findErr);
        }
      }

      if (!activeId) {
        setLotteryList(null);
        setLoading(false);
        return;
      }

      // 1. Fetch Main Lottery Info
      const lotteryRes = await cinstance.methods.lottery(activeId).call();
      if (!lotteryRes || !lotteryRes.lotteryId || lotteryRes.lotteryId === "0") {
        setLotteryList(null);
        setLoading(false);
        return;
      }
      setLotteryList(lotteryRes);

      // 2. Fetch Dates
      try {
        const datesRes = await cinstance.methods.lotteryDates(activeId).call();
        setDateList(datesRes);
      } catch (dErr) {
        console.error("Error fetching lottery dates:", dErr);
      }

      // 3. Fetch Partner Info if partnerAddress exists
      if (lotteryRes.partnerAddress && lotteryRes.partnerAddress !== "0x0000000000000000000000000000000000000000") {
        try {
          const partnerRes = await cinstance.methods
            .partnerbyaddr(lotteryRes.partnerAddress)
            .call();
          setPartnerDetails(partnerRes);
        } catch (pErr) {
          console.error("Error fetching partner details:", pErr);
        }
      }

      // 4. Fetch MineBET sold tickets if Type === 1
      if (lotteryRes.lotteryType === "1") {
        try {
          const mineSold = await cinstance.methods
            .getMineLotteryNumbers(activeId)
            .call();
          if (Array.isArray(mineSold)) {
            setAlreadySelectedMine(mineSold.map((it) => Number(it)));
          }
        } catch (mErr) {
          console.error("Error fetching Mine sold tickets:", mErr);
        }
      }
    } catch (err) {
      console.error("Error fetching lottery:", err);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [lotteryId]);

  // Fetch S3 Partner Logos
  useEffect(() => {
    let isCancelled = false;
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          if (!isCancelled) {
            const data = response?.data?.result || response?.data || [];
            setPartnerLogoData(Array.isArray(data) ? data : []);
          }
        })
        .catch((err) => {
          console.error("Partner logos fetch error:", err);
          if (!isCancelled) setPartnerLogoData([]);
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    checkAccount();
    fetchLotteryData();

    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => {
        setAccount(acc);
        setHasError(false);
      },
      onChainChanged: () => {
        checkAccount();
        fetchLotteryData();
      },
      onDisconnect: () => setAccount(""),
    });

    return unsubscribe;
  }, [checkAccount, fetchLotteryData]);

  // Handlers for MegaBET (Type 2)
  const handleNumberToggle = (num) => {
    const requiredPicks = Number(lotterylist?.pickNumbers) || 3;

    if (selectednum.includes(num)) {
      setSelectedNum((prev) => prev.filter((item) => item !== num));
    } else {
      if (selectednum.length >= requiredPicks) {
        setSelectedNum((prev) => [...prev.slice(0, requiredPicks - 1), num]);
      } else {
        setSelectedNum((prev) => [...prev, num]);
      }
    }
  };

  const handleQuickPickMega = () => {
    const capacity = Number(lotterylist?.capacity) || 20;
    const pickCount = Number(lotterylist?.pickNumbers) || 3;
    const pool = Array.from({ length: capacity }, (_, i) => i + 1);

    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    const randomPicks = pool.slice(0, pickCount).sort((a, b) => a - b);
    setSelectedNum(randomPicks);
    toast.info(`Quick Pick generated: ${randomPicks.join(", ")}`);
  };

  // Handlers for MineBET (Type 1)
  const handleSelectMineNumber = (num) => {
    if (selectednum.includes(num)) {
      setSelectedNum([]);
    } else {
      setSelectedNum([num]);
    }
  };

  const handleQuickPickMine = () => {
    const capacity = Number(lotterylist?.capacity) || 20;
    const soldSet = new Set(alreadySelectedMine.map((n) => Number(n)));
    const available = [];

    for (let i = 1; i <= capacity; i++) {
      if (!soldSet.has(i)) {
        available.push(i);
      }
    }

    if (available.length === 0) {
      toast.warning("All tickets for this Mine lottery have been sold.");
      return;
    }

    const randomIndex = Math.floor(Math.random() * available.length);
    const chosenTicket = available[randomIndex];
    setSelectedNum([chosenTicket]);
    toast.info(`Random Ticket #${chosenTicket} selected!`);
  };

  // Handlers for RocketBET / Missile (Type 3)
  const handleQuickPickMissile = () => {
    const pickCount = Number(lotterylist?.pickNumbers) || 3;
    let randomStr = "";
    for (let i = 0; i < pickCount; i++) {
      randomStr += Math.floor(Math.random() * 10).toString();
    }
    setSelectedMissileNumber(randomStr);
    toast.info(`Random Target ${randomStr} generated!`);
  };

  // Unified Buy Lottery Action
  const handleBuy = async () => {
    if (!window.ethereum) {
      toast.error("Web3 wallet not detected.");
      return;
    }

    const typeId = Number(lotterylist?.lotteryType);

    try {
      setTxLoading(true);
      window.web3 = web3;
      const accounts = await web3.eth.getAccounts();

      if (!accounts || accounts.length === 0) {
        await connectWallet();
        setTxLoading(false);
        return;
      }

      // 1. MineBET Purchase (Type 1)
      if (typeId === 1) {
        if (selectednum.length !== 1) {
          toast.warning("Please select 1 ticket number.");
          setTxLoading(false);
          return;
        }

        // Fresh check against double-sell
        const targetLotteryId = lotterylist?.lotteryId || lotteryId;
        const latestSold = await cinstance.methods
          .getMineLotteryNumbers(targetLotteryId)
          .call();
        const latestSoldNumbers = latestSold.map((it) => Number(it));
        setAlreadySelectedMine(latestSoldNumbers);

        if (latestSoldNumbers.includes(Number(selectednum[0]))) {
          setTxLoading(false);
          toast.error("This ticket was just purchased by another player. Please pick another!");
          setSelectedNum([]);
          return;
        }

        await cinstance.methods
          .buyNormalLottery(selectednum, targetLotteryId)
          .send({ from: accounts[0], value: lotterylist.entryFee });

        setSuccessMessage(`Ticket #${selectednum[0]} purchased successfully!`);
        setAlreadySelectedMine((prev) => [...prev, selectednum[0]]);
        setSelectedNum([]);
      }
      // 2. RocketBET / Missile Purchase (Type 3)
      else if (typeId === 3) {
        if (!selectedMissileNumber) {
          toast.warning("Please enter your launch target number.");
          setTxLoading(false);
          return;
        }

        const targetLotteryId = lotterylist?.lotteryId || lotteryId;
        await cinstance.methods
          .buyMissilelottery(selectedMissileNumber, targetLotteryId)
          .send({ from: accounts[0], value: lotterylist.entryFee });

        setSuccessMessage("Rocket launched successfully on-chain!");
      }
      // 3. MegaBET (Type 2)
      else {
        const requiredPicks = Number(lotterylist?.pickNumbers) || 3;
        if (selectednum.length !== requiredPicks) {
          toast.warning(`Please pick exactly ${requiredPicks} numbers.`);
          setTxLoading(false);
          return;
        }

        const targetLotteryId = lotterylist?.lotteryId || lotteryId;
        await cinstance.methods
          .buyNormalLottery(selectednum, targetLotteryId)
          .send({ from: accounts[0], value: lotterylist.entryFee });

        setSuccessMessage("Lottery ticket purchased successfully!");
      }

      setTxLoading(false);
      setShowSuccess(true);
      toast.success("Transaction completed successfully!");
    } catch (err) {
      console.error("Buy lottery error:", err);
      setTxLoading(false);
      setShowFailed(true);
      toast.error("Transaction failed or rejected.");
    }
  };

  const partnerLogoUrl = useMemo(() => {
    if (!partnerDetails?.logoHash) return "https://autobetlottery.com/static/media/logo.b714e7ab.png";
    return getImageUrlByHash(
      partnerDetails?.logoHash,
      Array.isArray(partnerLogoData) ? partnerLogoData : []
    );
  }, [partnerDetails, partnerLogoData]);

  const totalPrizeFormatted = useMemo(() => {
    return safeFromWei(lotterylist?.totalPrize);
  }, [lotterylist]);

  const entryFeeFormatted = useMemo(() => {
    return safeFromWei(lotterylist?.entryFee);
  }, [lotterylist]);

  const statusNum = Number(lotterylist?.status || 0);
  const typeId = Number(lotterylist?.lotteryType || 2);
  const effectiveLotteryId = lotterylist?.lotteryId || lotteryId;

  return (
    <React.Fragment>
      {/* {loading && <Loaders />}
      {hasError && !loading && <Warnings />} */}
      <NavBar />

      <Container maxWidth="lg" className="buylottery-page-container" sx={{ pt: { xs: "90px", md: "100px" }, pb: 6 }}>
        {loading ? (
          <Box sx={{ py: 2 }}>
            <Skeleton variant="rounded" height={160} sx={{ mb: 3, borderRadius: borderRadiusPx.surface }} />
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Skeleton variant="rounded" height={380} sx={{ borderRadius: borderRadiusPx.xxl }} />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Skeleton variant="rounded" height={380} sx={{ borderRadius: borderRadiusPx.xxl }} />
              </Grid>
            </Grid>
          </Box>
        ) : !lotterylist || !lotterylist.lotteryId || lotterylist.lotteryId === "0" ? (
          <BuyLotteryEmptyState lotteryId={lotteryId} />
        ) : (
          <Box sx={{ py: 2 }}>
            {/* Standard Header across all lottery types */}
            <LotteryHeader
              lotteryId={effectiveLotteryId}
              lottery={lotterylist}
              datelist={datelist}
              partnerDetails={partnerDetails}
              partnerLogoUrl={partnerLogoUrl}
              currency={currency}
              totalPrizeFormatted={totalPrizeFormatted}
            />

            {/* Check if Lottery is Open (status 0 or rollover open 5) */}
            {statusNum === 0 || statusNum === 5 ? (
              typeId === 0 ? (
                /* Type 0: SpinBET Wheel Arena */
                <SpinWheelView
                  lotteryData={lotterylist}
                  partner={partnerDetails}
                  partnerLogoData={partnerLogoData}
                />
              ) : (
                /* Type 1, 2, 3: Interactive Grid Layout with Sticky Checkout */
                <Grid container spacing={3.5}>
                  <Grid item xs={12} lg={8}>
                    {typeId === 1 ? (
                      /* Type 1: MineBET */
                      <MinePicker
                        capacity={lotterylist?.capacity || 20}
                        alreadySelected={alreadySelectedMine}
                        selectednum={selectednum}
                        onSelectNumber={handleSelectMineNumber}
                        onQuickPick={handleQuickPickMine}
                        onClear={() => setSelectedNum([])}
                      />
                    ) : typeId === 3 ? (
                      /* Type 3: RocketBET / Missile */
                      <MissilePicker
                        level={datelist?.level || 4}
                        pickNumbers={lotterylist?.pickNumbers || 3}
                        selectedNumber={selectedMissileNumber}
                        onSelectNumber={(val) => setSelectedMissileNumber(val)}
                        onQuickPick={handleQuickPickMissile}
                        onClear={() => setSelectedMissileNumber("")}
                      />
                    ) : (
                      /* Type 2: MegaBET / Classic */
                      <NumberPicker
                        capacity={lotterylist?.capacity || 20}
                        pickNumbers={lotterylist?.pickNumbers || 3}
                        selectednum={selectednum}
                        lotteryType={lotterylist?.lotteryType}
                        onNumberToggle={handleNumberToggle}
                        onQuickPick={handleQuickPickMega}
                        onClear={() => setSelectedNum([])}
                      />
                    )}

                    {/* Game-specific How It Works */}
                    <LotteryRulesInfo lotteryType={lotterylist?.lotteryType} />
                  </Grid>

                  {/* Right Column: Checkout Summary */}
                  <Grid item xs={12} lg={4}>
                    <LotterySummaryCard
                      lotteryId={effectiveLotteryId}
                      lotteryType={lotterylist?.lotteryType}
                      entryFeeFormatted={entryFeeFormatted}
                      currency={currency}
                      pickNumbers={lotterylist?.pickNumbers || 3}
                      selectednum={selectednum}
                      selectedMissileNumber={selectedMissileNumber}
                      account={account}
                      loading={txLoading}
                      onBuy={handleBuy}
                      onConnectWallet={connectWallet}
                    />
                  </Grid>
                </Grid>
              )
            ) : (
              /* Closed, Drawn or Inactive Status */
              <Box sx={{ maxWidth: 800, mx: "auto", my: 3 }}>
                <LotteryResultView
                  lottery={lotterylist}
                  totalPrizeFormatted={totalPrizeFormatted}
                  currency={currency}
                />
                <LotteryRulesInfo lotteryType={lotterylist?.lotteryType} />
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* Transaction Feedback Popups */}
      <SuccessPopup
        show={showSuccess}
        setShowModal={() => setShowSuccess(false)}
        message={successMessage}
        redirectPath={Path.profile}
        btnName={"Go to Profile"}
      />

      <FailedPopup
        show={showFailed}
        setShowModal={() => setShowFailed(false)}
      />
    </React.Fragment>
  );
};

export default BuyLottery;
